import { useEffect, useState } from 'react';
import routes from '@root/routes';
import API from '@shared/api';
import { Form } from '@ant-design/compatible';
import { Row, Col, Steps, Input, Select, notification } from 'antd';
import Button from '@components/shared/V2Button';
import Container from '@components/shared/Admin/Container';
import { formType, matchType } from '@propTypes';
import formatToPhoneNumber from '@shared/formatToPhoneNumber';
import formatPhoneNumber from '@shared/formatPhoneNumber';
import useEnums from '@shared/useEnums';
import { useTranslation } from 'react-i18next';
import { useBreadcrumbs } from '@components/shared/withBreadcrumbs';
import { useFlags } from 'launchdarkly-react-client-sdk';

const formItemLayout = {
  labelCol: {
    sm: { span: 2 },
  },
  wrapperCol: {
    sm: { span: 7 },
  },
};

const UserPage = ({
  match: {
    params: { id: userId },
  },
  form: { getFieldsValue, getFieldDecorator },
}) => {
  const { t } = useTranslation('admin');
  const flags = useFlags();

  const { setBreadcrumbs } = useBreadcrumbs();
  useEffect(() => {
    setBreadcrumbs([
      t('breadcrumbs.admin'),
      { content: t('breadcrumbs.users'), link: routes.admin.userSearch },
      t('breadcrumbs.userEdit'),
    ]);
  }, []);

  const { markets, roles: roleOptions } = useEnums();
  const [user, setUser] = useState(null);
  const fetchUser = async () => {
    const response = await API.fetch(routes.api.admin.user(userId));
    setUser(await response.json());
  };

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const submitForm = async event => {
    event.preventDefault();

    const response = await API.put(routes.api.admin.user(userId), getFieldsValue());
    if (response.ok) {
      notification.success({ message: t('user.successfulEdit') });
      fetchUser();
    } else {
      const { messages } = await response.json();
      notification.error({ message: messages.join(', ') });
    }
  };

  const { firstName, lastName, role } = getFieldsValue();

  if (!user) return null;
  return (
    <>
      <Row>
        <Col span={8}>
          <Steps current={1}>
            <Steps.Step
              title={t('user.steps.search.title')}
              description={t('user.steps.search.description')}
            />
            <Steps.Step
              title={t('user.steps.edit.title')}
              description={t('user.steps.edit.description')}
            />
          </Steps>
        </Col>
      </Row>
      <Container>
        <Form onSubmit={submitForm} {...formItemLayout}>
          <Form.Item label={t('user.idLabel')}>
            {getFieldDecorator('id', {
              initialValue: user.id,
            })(<Input disabled />)}
          </Form.Item>
          <Form.Item label={t('user.emailLabel')}>
            {getFieldDecorator('email', {
              initialValue: user.email,
            })(<Input disabled />)}
          </Form.Item>
          <Form.Item label={t('user.firstNameLabel')}>
            {getFieldDecorator('firstName', {
              initialValue: user.firstName,
              rules: [
                {
                  required: true,
                  message: t('user.firstNameValidation'),
                },
              ],
            })(<Input disabled />)}
          </Form.Item>
          <Form.Item label={t('user.lastNameLabel')}>
            {getFieldDecorator('lastName', {
              initialValue: user.lastName,
              rules: [
                {
                  required: true,
                  message: t('user.lastNameValidation'),
                },
              ],
            })(<Input disabled />)}
          </Form.Item>
          <Form.Item label={t('user.phoneNumberLabel')}>
            {getFieldDecorator('phoneNumber', {
              initialValue: user.phoneNumber,
              normalize: flags['phone-number-format'] ? formatPhoneNumber : formatToPhoneNumber,
            })(<Input disabled />)}
          </Form.Item>
          <Form.Item label={t('user.jobTitleLabel')}>
            {getFieldDecorator('jobTitle', {
              initialValue: user.jobTitle,
            })(<Input data-testid="jobTitle" disabled />)}
          </Form.Item>
          <Form.Item label={t('user.roleLabel')}>
            {getFieldDecorator('role', {
              initialValue: user.role,
              rules: [
                {
                  required: true,
                  message: t('user.roleValidation'),
                },
              ],
            })(
              <Select data-testid="role">
                {roleOptions.map(roleOption => (
                  <Select.Option key={roleOption} value={roleOption}>
                    {t(`common:roles.${roleOption}`)}
                  </Select.Option>
                ))}
              </Select>,
            )}
          </Form.Item>
          <Form.Item label={t('user.locationLabel')}>
            {getFieldDecorator('marketSlug', {
              initialValue: user.marketSlug,
            })(
              <Select data-testid="location">
                {markets.map(market =>
                  market.isPublic ? (
                    <Select.Option key={market.id} value={market.id}>
                      {flags['public-london-market']
                        ? t(`common:markets.${market.id}`)
                        : t(`common:deprecatedMarkets.${market.id}`)}
                    </Select.Option>
                  ) : null,
                )}
              </Select>,
            )}
          </Form.Item>
          <Button htmlType="submit" disabled={!firstName || !lastName || !role}>
            {t('common.saveButton')}
          </Button>
        </Form>
      </Container>
    </>
  );
};

UserPage.propTypes = {
  form: formType.isRequired,
  match: matchType.isRequired,
};

export default Form.create({ name: 'user_edit' })(UserPage);
